import {inject, Injectable, OnDestroy}          from '@angular/core';
import {Tenant, User}                           from '@src/app/admin/users/models/user.model';
import {environment}                            from '@src/environments/environment';
import * as Sentry                              from '@sentry/browser';
import * as LaunchDarkly                        from 'launchdarkly-js-client-sdk';
import {LDFlagValue}                            from 'launchdarkly-js-client-sdk';
import {Observable, of}                        from 'rxjs';
import {distinctUntilChanged, map, shareReplay, switchMap} from 'rxjs/operators';
import {BillingFeature}                        from '@src/app/core/enums/billing-feature.enum';
import {BillingPolicyService}                   from './billing-policy.service';

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagService implements OnDestroy
{
    public client: LaunchDarkly.LDClient;
    private readonly billingPolicyService = inject(BillingPolicyService);
    private readonly flagStreams = new Map<string, Observable<LDFlagValue>>();
    private contextFingerprint?: string;
    private policyFingerprint?: string;
    private activeTenantId?: string;
    private expiryTimeoutId?: number;

    public async initialize(user: User, tenant: Tenant): Promise<void>
    {
        if (!user?.id || !tenant?.id) {
            await this.closeClient();
            return;
        }

        const tenantId = String(tenant.id);
        const tenantChanged = this.activeTenantId !== tenantId;

        if (tenantChanged) {
            this.billingPolicyService.clearCurrent();
            this.activeTenantId = tenantId;
        }

        const context = this.buildContext(user, tenant);
        const fingerprint = JSON.stringify(context);
        const policyFingerprint = JSON.stringify({
            tenant_id: tenant.id,
            ...this.billingContext(tenant),
        });
        const policyChanged = this.policyFingerprint !== undefined && this.policyFingerprint !== policyFingerprint;
        this.policyFingerprint = policyFingerprint;

        if (policyChanged && !tenantChanged) {
            this.billingPolicyService.clearCurrent();
        }

        if (this.client) {
            if (this.contextFingerprint !== fingerprint) {
                await this.client.identify(context);
                this.contextFingerprint = fingerprint;
            }
        } else {
            this.client = LaunchDarkly.initialize(environment.launchDarkly.clientId, context, {inspectors: [Sentry.buildLaunchDarklyFlagUsedHandler()]});
            this.contextFingerprint = fingerprint;
        }

        this.scheduleGrandfatheringExpiry(user, tenant);
        await this.billingPolicyService.initialize(tenant.id, policyChanged);
    }

    private buildContext(user: User, tenant: Tenant): LaunchDarkly.LDMultiKindContext
    {
        const billing = this.billingContext(tenant);

        return {
            kind: 'multi',
            user: {
                key: `${user.id}`,
                name: user.name,
                email: user.email,
                type: user.type.title,
                anonymous: false,
                ...billing,
            },
            tenant: {
                key: `${tenant.id}`,
                name: tenant.name,
                status: tenant.status,
                ...billing,
            }
        } as LaunchDarkly.LDMultiKindContext;
    }

    private billingContext(tenant: Tenant)
    {
        return {
            early_adopter: tenant.early_adopter === true,
            grandfathering_active: tenant.grandfathering_active === true,
            grandfathering_status: tenant.grandfathering_status ?? 'ineligible',
            grandfathering_ends_at: tenant.grandfathering_ends_at ?? null,
            billing_plan: tenant.billing_plan ?? null,
        };
    }

    private scheduleGrandfatheringExpiry(user: User, tenant: Tenant): void
    {
        if (this.expiryTimeoutId !== undefined) {
            window.clearTimeout(this.expiryTimeoutId);
            this.expiryTimeoutId = undefined;
        }

        const grandfatheringEndsAt = tenant.grandfathering_ends_at;
        if (!tenant.early_adopter || tenant.grandfathering_status === 'expired' || !grandfatheringEndsAt) {
            return;
        }

        const grandfatheringEndsAtMs = new Date(grandfatheringEndsAt).getTime();
        const delay = grandfatheringEndsAtMs - Date.now();
        const expiredTenant = {
            ...tenant,
            grandfathering_active: false,
            grandfathering_status: 'expired',
            billing_plan: tenant.billing_plan === 'grandfathered_payg' ? 'payg' : tenant.billing_plan,
        };

        if (!Number.isFinite(delay) || delay <= 0) {
            void this.initialize(user, expiredTenant);
            return;
        }

        this.expiryTimeoutId = window.setTimeout(() => {
            if (grandfatheringEndsAtMs <= Date.now()) {
                void this.initialize(user, expiredTenant);
            } else {
                this.scheduleGrandfatheringExpiry(user, tenant);
            }
        }, Math.min(delay, 2147483647));
    }

    public getFlag(flagKey: string, defaultValue: LDFlagValue = false): Observable<LDFlagValue>
    {
        const billingFeature = BillingFeature[flagKey as keyof typeof BillingFeature];
        if (!this.client) {
            console.error('LaunchDarkly client is not initialized. Call initialize() first.');
            return of(billingFeature ? false : defaultValue);
        }

        const cached = this.flagStreams.get(flagKey);
        if (cached) {
            return cached;
        }

        const rolloutStream = this.getRolloutFlag(flagKey, defaultValue);
        const stream = (billingFeature
                ? this.billingPolicyService.policy$.pipe(
                    switchMap(policy => {
                        if (!this.billingPolicyService.isFeatureAllowed(billingFeature)) return of(false);
                        const rolloutEnabled = policy?.rollout_flags?.[billingFeature];
                        return rolloutEnabled === undefined ? of(true) : rolloutEnabled ? rolloutStream.pipe(map(Boolean)) : of(false);
                    }),
                )
                : rolloutStream
        ).pipe(
            distinctUntilChanged(),
            shareReplay({bufferSize: 1, refCount: true}),
        );

        this.flagStreams.set(flagKey, stream);

        return stream;
    }

    /** Rollout availability without the paid entitlement check (for standard-form site workflows). */
    public getRolloutFlag(flagKey: string, defaultValue: LDFlagValue = false): Observable<LDFlagValue>
    {
        if (!this.client) {
            return of(defaultValue);
        }

        const cacheKey = `rollout:${flagKey}`;
        const cached = this.flagStreams.get(cacheKey);
        if (cached) return cached;

        const stream = new Observable<LDFlagValue>((observer) => {
            const emit = (): void => {
                observer.next(this.client.variation(flagKey, defaultValue));
            };

            const changeHandler = (): void => {
                emit();
            };

            this.client.on(`change:${flagKey}`, changeHandler);
            this.client.waitUntilReady().then(emit).catch(() => observer.next(defaultValue));

            return () => {
                this.client.off(`change:${flagKey}`, changeHandler);
            };
        }).pipe(
            distinctUntilChanged(),
            shareReplay({bufferSize: 1, refCount: true}),
        );

        this.flagStreams.set(cacheKey, stream);

        return stream;
    }

    public async ngOnDestroy(): Promise<void>
    {
        await this.closeClient();
    }

    private async closeClient(): Promise<void>
    {
        this.flagStreams.clear();
        this.contextFingerprint = undefined;
        this.policyFingerprint = undefined;
        this.activeTenantId = undefined;
        this.billingPolicyService.clearCurrent();

        if (this.expiryTimeoutId !== undefined) {
            window.clearTimeout(this.expiryTimeoutId);
            this.expiryTimeoutId = undefined;
        }

        if (this.client) {
            await this.client.close();
            this.client = undefined;
        }
    }
}
